import React, { useState, useEffect, Fragment } from 'react';
import PropTypes, { object } from 'prop-types';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import {
  Table,
  SortingIcon,
  TableFilterFromTo,
  EmptyState,
  Loading
} from 'components/common/Elements/Elements';
import TableRow from './parts/TableRow/TableRow';

import useWindowSize from 'hooks/useWindowSize';
import { calcColumnShadow, whoIslast } from 'utils/screenTableCalc';

import classes from './AllTenantsTable.module.scss';

const AllTenantsTable = ({
  tableData = [],
  handleSortable,
  loader,
  tableConf,
  handleFromTo,
  filter,
  requestForFilteredData
}) => {
  const { direction, fieldName } = filter;
  const { t } = useTranslation(['activity']);

  const [row, setRow] = useState('');
  const [columnShadow, setColumnShadow] = useState(true);
  const [firstColumnAbs, setFirstColumnAbs] = useState(true);
  const [lastColumn, setLastColumn] = useState(t('activity:filters.INDUSTRY'));
  const [hoverColumn, setHoverColumn] = useState('');
  const size = useWindowSize();

  const fieldFormat = field => field.replace('.', '_');

  const mouseEnter = e => {
    setRow(e.currentTarget.getAttribute('r'));
    setHoverColumn(e.target.innerText);
  };
  const mouseLeave = () => {
    setRow('');
    setHoverColumn('');
  };

  useEffect(() => {
    const isColumnShadow = calcColumnShadow(tableConf, size.width);
    setColumnShadow(isColumnShadow);
  }, [size, tableConf]);

  useEffect(() => {
    const quantityColumns = tableConf.reduce(function (
      accumulator,
      currentValue
    ) {
      if (currentValue.show) return accumulator + 1;
      return accumulator;
    },
    0);
    if (quantityColumns === 1) setFirstColumnAbs(false);
    else setFirstColumnAbs(true);
  }, [tableConf]);

  const handleScroll = e => {
    if (columnShadow) {
      setLastColumn(whoIslast(e.target, tableConf));
    } else {
      setLastColumn('');
    }
  };

  return (
    <>
      <div className={classes.outer}>
        <div
          className={cn(
            classes.inner,
            columnShadow ? classes.inner_shadow : classes.inner_nowidth,

            !firstColumnAbs && classes.inner_noabs,
            lastColumn === hoverColumn && classes.inner_pad
          )}
          onScroll={handleScroll}
        >
          <Table className={classes.table}>
            <Table.Header>
              <Table.Row
                withBorder={false}
                className={cn(classes.row, classes.row_header)}
              >
                {tableConf.map(
                  ({ isSortable, field, title, fromToGroup, show }, ind) =>
                    show && (
                      <Table.HeaderCell
                        key={field}
                        className={cn(
                          classes.headerCell,
                          classes[fieldFormat(field)]
                        )}
                        onMouseEnter={mouseEnter}
                        onMouseLeave={mouseLeave}
                        r={`r${ind}`}
                        title={title}
                      >
                        {fromToGroup && (
                          <TableFilterFromTo
                            fromTo={filter[field]}
                            handleFromTo={handleFromTo}
                            field={field}
                          />
                        )}
                        {isSortable && (
                          <SortingIcon
                            direction={direction}
                            field={field}
                            fieldName={fieldName}
                            onClick={handleSortable(field)}
                          />
                        )}
                      </Table.HeaderCell>
                    )
                )}
              </Table.Row>
            </Table.Header>
            {loader || (tableData && tableData.length === 0) ? (
              <Table.Body></Table.Body>
            ) : (
              <Table.Body>
                {tableData.map(company => (
                  <Fragment key={company.customerId}>
                    <TableRow
                      company={company}
                      family={company.childs && 'parent'}
                      row={row}
                      mouseEnter={mouseEnter}
                      tableConf={tableConf}
                      requestForFilteredData={requestForFilteredData}
                    />
                    {company.childs &&
                      company.childs.map((childs, ind) => (
                        <Fragment
                          key={`${company.customerId}-${childs.customerId}`}
                        >
                          <TableRow
                            company={childs}
                            family={childs.childs ? 'child-parent' : 'child'}
                            noLast={company.childs.length !== ind + 1}
                            isOtherGrands={
                              childs.childs && company.childs.length > ind + 1
                            }
                            row={row}
                            mouseEnter={mouseEnter}
                            tableConf={tableConf}
                          />
                          {childs.childs &&
                            childs.childs.map((child, idx) => (
                              <TableRow
                                company={child}
                                key={`${childs.customerId}-${child.customerId}`}
                                family="grandson"
                                noLast={
                                  company.childs[ind].childs.length !== idx + 1
                                }
                                row={row}
                                mouseEnter={mouseEnter}
                                isOtherGrands={company.childs.length > ind + 1}
                                tableConf={tableConf}
                              />
                            ))}
                        </Fragment>
                      ))}
                  </Fragment>
                ))}
              </Table.Body>
            )}
          </Table>
        </div>
      </div>
      {loader ? <Loading /> : tableData.length === 0 && <EmptyState />}
    </>
  );
};

AllTenantsTable.propTypes = {
  tableData: PropTypes.arrayOf(object),
  handleSortable: PropTypes.func,
  loader: PropTypes.bool,
  tableConf: PropTypes.arrayOf(object),
  handleFromTo: PropTypes.func,
  filter: PropTypes.objectm,
  requestForFilteredData: PropTypes.func.isRequired
};

export default AllTenantsTable;
